import { createContext, useContext, useEffect, useRef, useState } from "react";
import { api, setAuthTokens, clearAuthTokens, getAccessToken } from "@/lib/api";
import { resetGuestBrandCache } from "@/lib/guestBrand";

const AuthContext = createContext(null);
const IDLE_TIMEOUT_MS = 20 * 60 * 1000; // 20 minutes
const IDLE_WARNING_MS = 60 * 1000;      // last 1 minute — surface a warning

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [ready, setReady] = useState(false);
  const [idleWarning, setIdleWarning] = useState(false);
  // Login flow can pause on MFA — remembered so the UI can render a code prompt.
  const [mfaChallenge, setMfaChallenge] = useState(null);
  // Or on a forced password change (admin-provisioned temp password).
  const [pwChangeChallenge, setPwChangeChallenge] = useState(null);
  const idleTimer = useRef(null);
  const warningTimer = useRef(null);

  useEffect(() => {
    if (!getAccessToken()) {
      setReady(true);
      return;
    }
    api
      .get("/auth/me")
      .then((r) => {
        setUser(r.data.user);
        setProfile(r.data.profile);
      })
      .catch(() => clearAuthTokens())
      .finally(() => setReady(true));
  }, []);

  useEffect(() => {
    if (!user) return;
    const reset = () => {
      setIdleWarning(false);
      if (idleTimer.current) clearTimeout(idleTimer.current);
      if (warningTimer.current) clearTimeout(warningTimer.current);
      warningTimer.current = setTimeout(() => setIdleWarning(true), IDLE_TIMEOUT_MS - IDLE_WARNING_MS);
      idleTimer.current = setTimeout(() => {
        clearAuthTokens();
        setUser(null);
        setProfile(null);
        setIdleWarning(false);
      }, IDLE_TIMEOUT_MS);
    };
    const events = ["mousemove", "keydown", "click", "scroll", "touchstart"];
    events.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    reset();
    return () => {
      events.forEach((e) => window.removeEventListener(e, reset));
      if (idleTimer.current) clearTimeout(idleTimer.current);
      if (warningTimer.current) clearTimeout(warningTimer.current);
    };
  }, [user]);

  const _acceptSession = (data) => {
    // Backend refuses a session while a temporary password is in play — it
    // hands back a scoped token that can only reach /auth/change-password.
    if (data.must_change_password) {
      setPwChangeChallenge({ token: data.password_change_token, email: data.email, role: data.role });
      setMfaChallenge(null);
      return { must_change_password: true, role: data.role };
    }
    setAuthTokens(data);
    // Fresh login session — privacy notice is due once (consumed by PrivacyBanner).
    sessionStorage.setItem("aura_privacy_pending", "1");
    setUser(data.user);
    setProfile(data.profile);
    setMfaChallenge(null);
    setPwChangeChallenge(null);
    return data.user;
  };

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.mfa_required) {
      // Return an object signalling the UI to render the MFA prompt
      setMfaChallenge({ token: data.mfa_challenge_token, role: data.role });
      return { mfa_required: true, role: data.role };
    }
    return _acceptSession(data);
  };

  const changePassword = async (currentPassword, newPassword) => {
    if (!pwChangeChallenge) throw new Error("No password change in progress");
    const { data } = await api.post("/auth/change-password", {
      password_change_token: pwChangeChallenge.token,
      current_password: currentPassword,
      new_password: newPassword,
    });
    return _acceptSession(data);
  };

  const cancelPasswordChange = () => setPwChangeChallenge(null);

  const verifyMfa = async (code) => {
    if (!mfaChallenge) throw new Error("No MFA challenge in progress");
    const { data } = await api.post("/auth/mfa/verify", {
      mfa_challenge_token: mfaChallenge.token,
      code,
    });
    return _acceptSession(data);
  };

  const cancelMfa = () => setMfaChallenge(null);

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    return _acceptSession(data);
  };

  const acceptSsoResult = async (data) => _acceptSession(data);

  const stayLogin = async ({ room, surname, code, hotel_id }) => {
    const { data } = await api.post("/auth/guest/stay-login", { room, surname, code, hotel_id });
    return _acceptSession(data);
  };

  const logout = async () => {
    const refresh = localStorage.getItem("aura_refresh_token");
    if (refresh) {
      try { await api.post("/auth/logout", { refresh_token: refresh }); } catch { /* ignore */ }
    }
    clearAuthTokens();
    resetGuestBrandCache();
    sessionStorage.removeItem("aura_privacy_pending");
    setUser(null);
    setProfile(null);
    setMfaChallenge(null);
    setPwChangeChallenge(null);
  };

  return (
    <AuthContext.Provider value={{
      user, profile, ready, idleWarning,
      login, register, logout,
      mfaChallenge, verifyMfa, cancelMfa,
      pwChangeChallenge, changePassword, cancelPasswordChange,
      acceptSsoResult, stayLogin,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
